import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../config/api';

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '500', label: '500' },
  { value: '1000', label: '1,000' },
  { value: 'all', label: '전체' },
];

function formatCellValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function SystemDbBrowserView() {
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState('10');
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getToken = () => localStorage.getItem('authToken');

  const apiGet = async (path) => {
    const token = getToken();
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });

    const data = await response.json();

    if (!(response.ok && data.status === 'success')) {
      throw new Error(data.message || '요청에 실패했습니다.');
    }

    return data;
  };

  const fetchObjects = async () => {
    try {
      setLoadingObjects(true);
      setErrorMessage('');
      const data = await apiGet('/api/system-db/browser/objects');
      const nextObjects = data.objects || [];
      setObjects(nextObjects);
      setSelectedObject((prev) => prev || nextObjects[0]?.name || '');
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '테이블 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingObjects(false);
    }
  };

  const fetchRows = async (objectName, nextPage = 1) => {
    if (!objectName) {
      setColumns([]);
      setRows([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoadingRows(true);
      setErrorMessage('');
      const isAll = pageSize === 'all';
      const limitParam = isAll ? 'all' : pageSize;
      const offset = isAll ? 0 : (nextPage - 1) * Number(pageSize);
      const data = await apiGet(
        `/api/system-db/browser/rows?object=${encodeURIComponent(objectName)}&limit=${limitParam}&offset=${offset}`
      );
      setColumns(data.columns || []);
      setRows(data.rows || []);
      setTotalCount(Number(data.pagination?.total_count || 0));
      setPage(isAll ? 1 : nextPage);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '테이블 데이터를 불러오지 못했습니다.');
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    fetchObjects();
  }, []);

  useEffect(() => {
    if (!selectedObject) return;
    fetchRows(selectedObject, 1);
  }, [selectedObject, pageSize]);

  const isAllMode = pageSize === 'all';
  const numericPageSize = isAllMode ? totalCount || rows.length || 1 : Number(pageSize);
  const totalPages = isAllMode ? 1 : Math.max(1, Math.ceil(totalCount / numericPageSize));
  const selectedMeta = objects.find((item) => item.name === selectedObject);

  return (
    <div style={{ display: 'grid', gap: '20px', minWidth: 0 }}>
      <div className="dashboard-panel dashboard-panel-wide" style={{ minWidth: 0 }}>
        <div className="dashboard-panel-header">
          <h3>계통 DB 데이터 탐색</h3>
        </div>

        <p className="dashboard-section-description" style={{ marginBottom: '16px' }}>
          실제 적재된 시스템 DB 테이블과 뷰 데이터를 테이블별로 읽기 전용 확인합니다.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 420px) minmax(0, 1fr)',
            gap: '16px',
            alignItems: 'end',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'grid', gap: '8px', minWidth: 0 }}>
            <label htmlFor="system-db-object-select" style={{ fontWeight: 700 }}>
              조회 대상
            </label>
            <select
              id="system-db-object-select"
              value={selectedObject}
              onChange={(e) => setSelectedObject(e.target.value)}
              disabled={loadingObjects}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#fff',
                color: '#0f172a',
              }}
            >
              {objects.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.label} ({item.type}, {Number(item.row_count || 0).toLocaleString()}건)
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
            <label htmlFor="system-db-page-size-select" style={{ fontSize: '14px', color: '#94a3b8' }}>
              조회 개수
            </label>
            <select
              id="system-db-page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              disabled={loadingRows}
              style={{
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#fff',
                color: '#0f172a',
              }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button type="button" className="dashboard-secondary-btn" onClick={fetchObjects}>
              목록 갱신
            </button>
            <button
              type="button"
              className="dashboard-secondary-btn"
              onClick={() => fetchRows(selectedObject, page)}
              disabled={!selectedObject}
            >
              데이터 갱신
            </button>
            <span style={{ color: '#94a3b8', fontSize: '14px', minWidth: 0, wordBreak: 'break-word' }}>
              {selectedMeta
                ? `${selectedMeta.label} · ${selectedMeta.type} · 총 ${Number(totalCount || selectedMeta.row_count || 0).toLocaleString()}건`
                : '조회 대상을 선택하세요.'}
            </span>
          </div>
        </div>

        {errorMessage && (
          <div className="grid-upload-error" style={{ marginTop: '16px' }}>
            {errorMessage}
          </div>
        )}
      </div>

      <div className="dashboard-panel dashboard-panel-wide" style={{ minWidth: 0 }}>
        <div className="dashboard-panel-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
          <h3>데이터 미리보기</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', color: '#94a3b8' }}>
              {isAllMode ? '전체 조회' : `${page} / ${totalPages} 페이지`}
            </span>
            <button
              type="button"
              className="dashboard-secondary-btn"
              onClick={() => fetchRows(selectedObject, page - 1)}
              disabled={isAllMode || page <= 1 || loadingRows}
            >
              이전
            </button>
            <button
              type="button"
              className="dashboard-secondary-btn"
              onClick={() => fetchRows(selectedObject, page + 1)}
              disabled={isAllMode || page >= totalPages || loadingRows}
            >
              다음
            </button>
          </div>
        </div>

        <div className="dashboard-table-wrap system-db-browser-table-wrap">
          <table className="dashboard-table system-db-browser-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} style={{ textAlign: 'center', padding: '24px' }}>
                    {loadingRows ? '데이터를 불러오는 중입니다.' : '표시할 데이터가 없습니다.'}
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => (
                  <tr key={`${selectedObject}-${rowIndex}`}>
                    {columns.map((column) => {
                      const cellValue = formatCellValue(row[column]);
                      return (
                        <td key={`${selectedObject}-${rowIndex}-${column}`} title={cellValue}>
                          <span className="system-db-browser-cell">
                            {cellValue}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
